import type { AdPlacement } from '@openx/types/adUnit';
import type { DistributionChannel } from '@openx/types/distributionChannel';
import { type FormatTypeData, InventoryFormatTypeOption } from '@openx/types/targeting/formatType';
import type { TargetingProps } from '@openx/types/targeting/targeting';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { CtvValidationReturnType } from '../shared/CtvValidation/utils/useCtvValidation';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer';
import { FormatTypeReadOnly } from './FormatTypeReadOnly';
import { Items } from './Items';
import { TopBox } from './TopBox';
import { checkForEmptyParams } from './utils';

type CtvValidationMethods = Omit<
  CtvValidationReturnType,
  'setTechnographic' | 'isTechnographicOpen' | 'shouldDisplayWarningInTechnographic'
>;

const defaultCustomValidationMethods: CtvValidationMethods = {
  isFormatTypeOpen: false,
  setFormatType: noop,
  setIsFormatTypeOpen: noop,
  setIsTechnographicOpen: noop,
  shouldDisplayWarningInFormatType: false,
};

export const FormatType = memo(function FormatType({
  targetingParams,
  onFieldUpdate = noop,
  name,
  readonly,
  field,
  customOptions,
  customValidationMethods = defaultCustomValidationMethods,
  displayContext,
}: TargetingProps<FormatTypeData, CtvValidationMethods>) {
  const initState = useMemo(() => structuredClone(targetingParams), [targetingParams]);
  const [params, setParams] = useState<FormatTypeData | null>(initState);
  const [distributionChannel, setDistributionChannel] = useState<DistributionChannel | null>(null);
  const [adPlacement, setAdPlacement] = useState<AdPlacement | null>(null);

  const { setFormatType, setIsFormatTypeOpen, isFormatTypeOpen, ...methods } = customValidationMethods;

  useEffect(() => {
    setFormatType(params);
  }, [params, setFormatType]);

  const areParamsEmpty = useMemo(() => checkForEmptyParams(params), [params]);

  const isDataUpdated = useMemo(
    () =>
      !(
        isEqual(targetingParams, params) ||
        (targetingParams?.op === Criteria.ANY && params === null) ||
        (targetingParams === null && areParamsEmpty)
      ),
    [areParamsEmpty, params, targetingParams]
  );

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    onApply: () => {
      return onFieldUpdate(field, areParamsEmpty ? null : params);
    },
    restoreParams: () => setParams(initState && { ...initState }),
  });

  const onRemoveAll = useCallback(() => {
    onFieldUpdate(field, null);
    setParams(null);
  }, [onFieldUpdate, field]);

  const onRemoveParameter = useCallback(
    (opt: InventoryFormatTypeOption) => {
      if (!targetingParams) return;

      const next: FormatTypeData = {
        ...structuredClone(targetingParams),
        [opt]: null,
      };

      const nextIsEmpty = checkForEmptyParams(next);
      onFieldUpdate(field, nextIsEmpty ? null : next);
      setParams(nextIsEmpty ? null : next);

      if (opt === InventoryFormatTypeOption.DISTRIBUTION_CHANNEL) setDistributionChannel(null);
      if (opt === InventoryFormatTypeOption.AD_PLACEMENT) setAdPlacement(null);
    },
    [field, onFieldUpdate, targetingParams]
  );

  useEffect(() => {
    if (isOpen) {
      setDistributionChannel(targetingParams?.[InventoryFormatTypeOption.DISTRIBUTION_CHANNEL]?.val || null);
      setAdPlacement(targetingParams?.[InventoryFormatTypeOption.AD_PLACEMENT]?.val || null);
    }
  }, [isOpen, targetingParams]);

  return (
    <>
      <FormatTypeReadOnly
        name={name}
        field={field}
        readonly={readonly}
        targetingParams={targetingParams}
        onOpenDrawerClick={() => {
          onDrawerOpen();
          setIsFormatTypeOpen(true);
        }}
        onRemoveClick={onRemoveAll}
        customValidationMethods={methods}
        displayContext={displayContext}
        onRemoveParameter={onRemoveParameter}
      />

      {(isOpen || isFormatTypeOpen) && (
        <TargetingDrawer
          name={name}
          onClose={() => {
            onDrawerClose();
            setIsFormatTypeOpen(false);
          }}
          onApply={() => {
            onDrawerApply();
            setIsFormatTypeOpen(false);
          }}
          isApplyDisabled={!isDataUpdated}
        >
          <TopBox
            distributionChannel={distributionChannel}
            adPlacement={adPlacement}
            targetingParams={params}
            setDistributionChannel={setDistributionChannel}
            setAdPlacement={setAdPlacement}
            updateParams={setParams}
            customOptions={customOptions}
            displayContext={displayContext}
          />
          <Items
            targetingParams={params}
            readonly={false}
            updateParams={setParams}
            setDistributionChannel={setDistributionChannel}
            setAdPlacement={setAdPlacement}
            displayContext={displayContext}
          />
        </TargetingDrawer>
      )}
    </>
  );
});
